import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return 'Rp.\u00a00';
  const hasFraction = Number(amount) % 1 !== 0;
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: hasFraction ? 2 : 0
  }).format(amount).replace('Rp', 'Rp.');
};

const ShareholderCell = ({ shareholders, field, type = 'text' }) => {
  if (!shareholders || shareholders.length === 0) {
    return <span className="text-slate-400">-</span>;
  }

  return (
    <div>
      {shareholders.map((sh, idx) => {
        const value = sh[field];
        let content;
        let className = `py-1 ${idx > 0 ? 'border-t border-slate-300' : ''}`;

        if (type === 'percentage') {
          content = value > 0 ? `${value.toFixed(2)}%` : '-';
          className += value > 0 ? '' : ' text-slate-400';
        } else if (type === 'currency') {
          content = sh.percentage > 0 ? formatCurrency(value) : '-';
          className += sh.percentage > 0 
            ? (value >= 0 ? ' text-green-600 font-semibold' : ' text-red-600 font-semibold')
            : ' text-slate-400';
        } else {
          content = value;
        }

        return <div key={idx} className={className}>{content}</div>;
      })}
    </div>
  );
};

const ProfitLossTable = ({ 
  branches, 
  periodData, 
  title = 'Laba Rugi',
  colorScheme = 'green'
}) => {
  const colors = colorScheme === 'teal' ? {
    border: 'border-teal-200',
    headerBg: 'from-teal-50 to-blue-50',
    tableBg: 'bg-teal-100',
    tableBorder: 'border-teal-600',
    footerBg: 'bg-teal-50'
  } : {
    border: 'border-green-200',
    headerBg: 'from-green-50 to-emerald-50',
    tableBg: 'bg-green-100',
    tableBorder: 'border-green-600',
    footerBg: 'bg-green-50'
  };

  const totalProfitLoss = branches.reduce((sum, b) => sum + b.profitLoss, 0);
  const totalMitraDividend = branches.reduce((sum, b) => sum + b.mitraDividend, 0);
  const totalInvestorDividend = branches.reduce((sum, b) => sum + b.investorDividend, 0);

  return (
    <Card className={`border-2 ${colors.border}`}>
      <CardHeader className={`bg-gradient-to-r ${colors.headerBg}`}>
        <CardTitle className="text-xl font-bold text-slate-800">
          {title}{periodData ? ` - ${periodData.name}` : ''}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className={`${colors.tableBg} border-b-2 ${colors.tableBorder}`}>
                <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">No.</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Cabang</th>
                <th className="px-4 py-3 text-right text-sm font-bold text-slate-700">Laba Rugi</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Mitra</th>
                <th className="px-4 py-3 text-center text-sm font-bold text-slate-700">Saham</th>
                <th className="px-4 py-3 text-right text-sm font-bold text-slate-700">Dividen</th>
                <th className="px-4 py-3 text-right text-sm font-bold text-slate-700">Dividen Investor</th>
              </tr>
            </thead>
            <tbody>
              {branches.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-slate-500">
                    Tidak ada data cabang
                  </td>
                </tr>
              ) : (
                branches.map((branch, index) => (
                  <tr 
                    key={branch.branchId} 
                    className={`border-b hover:bg-slate-50 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
                  >
                    <td className="px-4 py-3 text-sm text-slate-700">{index + 1}</td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-900">{branch.branchName}</td>
                    <td className={`px-4 py-3 text-sm text-right font-semibold ${
                      branch.profitLoss >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(branch.profitLoss)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      <ShareholderCell shareholders={branch.shareholders} field="manager_name" />
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-slate-700">
                      <ShareholderCell shareholders={branch.shareholders} field="percentage" type="percentage" />
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <ShareholderCell shareholders={branch.shareholders} field="dividend" type="currency" />
                    </td>
                    <td className={`px-4 py-3 text-sm text-right font-semibold ${
                      branch.investorDividend >= 0 ? 'text-blue-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(branch.investorDividend)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {branches.length > 0 && (
              <tfoot>
                <tr className={`${colors.footerBg} border-t-2 ${colors.tableBorder} font-bold`}>
                  <td colSpan="2" className="px-4 py-3 text-sm text-slate-800">TOTAL</td>
                  <td className={`px-4 py-3 text-sm text-right ${
                    totalProfitLoss >= 0 ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {formatCurrency(totalProfitLoss)}
                  </td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3"></td>
                  <td className={`px-4 py-3 text-sm text-right ${
                    totalMitraDividend >= 0 ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {formatCurrency(totalMitraDividend)}
                  </td>
                  <td className={`px-4 py-3 text-sm text-right ${
                    totalInvestorDividend >= 0 ? 'text-blue-700' : 'text-red-700'
                  }`}>
                    {formatCurrency(totalInvestorDividend)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProfitLossTable;
